import { useState, useEffect, useCallback } from "react";
import { useApi } from "../hooks/useApi";
import Sidebar from "../components/Sidebar";
import { Topbar, PageError, friendlyError } from "../components";

interface Job {
  id:            string;
  connector_id:  string;
  triggered_by:  string;
  status:        "queued" | "running" | "complete" | "failed";
  data_types:    string[];
  total_records: Record<string, number> | null;
  total_bytes:   string | null;
  error_message: string | null;
  queued_at:     string;
  started_at:    string | null;
  completed_at:  string | null;
}

function formatBytes(bytes: string | null): string {
  if (!bytes) return "—";
  const b = parseInt(bytes);
  if (b < 1024) return `${b} B`;
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(1)} KB`;
  if (b < 1024 * 1024 * 1024) return `${(b / 1024 / 1024).toFixed(1)} MB`;
  return `${(b / 1024 / 1024 / 1024).toFixed(2)} GB`;
}

function formatDuration(start: string | null, end: string | null): string {
  if (!start || !end) return "—";
  const secs = Math.round((new Date(end).getTime() - new Date(start).getTime()) / 1000);
  if (secs < 60) return `${secs}s`;
  return `${Math.floor(secs / 60)}m ${secs % 60}s`;
}

function formatTime(ts: string): string {
  const d = new Date(ts);
  return d.toLocaleString("en-GB", {
    day: "2-digit", month: "short", year: "numeric",
    hour: "2-digit", minute: "2-digit",
  });
}

function totalRecords(records: Record<string, number> | null): string {
  if (!records) return "—";
  const total = Object.values(records).reduce((a, b) => a + b, 0);
  return total.toLocaleString();
}

function StatusBadge({ status }: { status: Job["status"] }) {
  const map: Record<string, [string, string]> = {
    complete: ["badge badge-success", "complete"],
    running:  ["badge badge-info",    "running"],
    queued:   ["badge badge-neutral", "queued"],
    failed:   ["badge badge-error",   "failed"],
  };
  const [cls, label] = map[status] ?? ["badge badge-neutral", status];
  return <span className={cls}>{label}</span>;
}

export default function HistoryPage() {
  const { get, post } = useApi();
  const [jobs, setJobs]       = useState<Job[]>([]);
  const [total, setTotal]     = useState(0);
  const [loading, setLoading] = useState(true);
  const [offset, setOffset]   = useState(0);
  const [expanded, setExpanded] = useState<string | null>(null);
  const [triggering, setTriggering] = useState(false);
  const [error, setError]     = useState<string | null>(null);
  const limit = 20;

  const load = useCallback(() => {
    setLoading(true);
    get<{ jobs: Job[]; total: number }>(`/api/jobs?limit=${limit}&offset=${offset}`)
      .then(r => { setJobs(r.jobs); setTotal(r.total); })
      .catch(err => setError(friendlyError(err)))
      .finally(() => setLoading(false));
  }, [get, offset]);

  useEffect(() => { load(); }, [load]);

  // Poll for running/queued jobs
  useEffect(() => {
    const hasActive = jobs.some(j => j.status === "queued" || j.status === "running");
    if (!hasActive) return;
    const t = setTimeout(load, 5000);
    return () => clearTimeout(t);
  }, [jobs, load]);

  const triggerBackup = async () => {
    setTriggering(true);
    try {
      const connRes = await get<{ connectors: any[] }>("/api/connectors");
      const c = connRes.connectors?.[0];
      if (!c) return;
      await post("/api/jobs", {
        connector_id: c.id,
        triggered_by: "manual",
        data_types: ["invoices", "bills", "contacts", "expenses", "bank_transactions", "attachments"],
      });
      setTimeout(load, 1000);
    } finally {
      setTriggering(false);
    }
  };

  return (
    <div className="shell">
      <Sidebar />
      <div className="main-column">
        <Topbar
          title="Backup History"
          subtitle={`${total} jobs total`}
          action={
            <button className="btn btn-primary btn-sm" onClick={triggerBackup} disabled={triggering}>
              {triggering ? "Queuing…" : "+ Run backup"}
            </button>
          }
        />
        <main className="page">
          {error && <PageError message={error} onRetry={load} />}

          {!error && (loading && jobs.length === 0 ? (
            <div style={{ textAlign: "center", color: "var(--text3)", padding: "60px 0" }}>Loading…</div>
          ) : jobs.length === 0 ? (
            <div style={{ textAlign: "center", color: "var(--text3)", padding: "60px 0" }}>
              <div style={{ fontSize: 32, marginBottom: 12, opacity: 0.2 }}>⬡</div>
              <p style={{ fontWeight: 500, color: "var(--text2)" }}>No backup jobs yet</p>
              <p style={{ fontSize: 13, marginTop: 4 }}>Run your first backup to see history here</p>
            </div>
          ) : (
            <>
              <div className="table-wrap">
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>Started</th>
                      <th>Status</th>
                      <th>Triggered by</th>
                      <th>Records</th>
                      <th>Size</th>
                      <th>Duration</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {jobs.map(job => (
                      <>
                        <tr
                          key={job.id}
                          className={expanded === job.id ? "row-expanded" : ""}
                          style={{ cursor: "pointer" }}
                          onClick={() => setExpanded(expanded === job.id ? null : job.id)}
                        >
                          <td style={{ fontFamily: "var(--mono)", fontSize: 12 }}>
                            {formatTime(job.queued_at)}
                          </td>
                          <td><StatusBadge status={job.status} /></td>
                          <td style={{ fontSize: 13, color: "var(--text2)" }}>{job.triggered_by}</td>
                          <td style={{ fontFamily: "var(--mono)", fontSize: 12 }}>{totalRecords(job.total_records)}</td>
                          <td style={{ fontFamily: "var(--mono)", fontSize: 12 }}>{formatBytes(job.total_bytes)}</td>
                          <td style={{ fontFamily: "var(--mono)", fontSize: 12 }}>{formatDuration(job.started_at, job.completed_at)}</td>
                          <td style={{ textAlign: "right" }}>
                            <span style={{ fontSize: 11, color: "var(--text3)" }}>{expanded === job.id ? "▲" : "▼"}</span>
                          </td>
                        </tr>
                        {expanded === job.id && (
                          <tr key={`${job.id}-detail`} className="row-detail">
                            <td colSpan={7}>
                              <div style={{ padding: "12px 4px", display: "flex", flexWrap: "wrap", gap: 8 }}>
                                {job.error_message && (
                                  <div className="alert alert-error" style={{ width: "100%", marginBottom: 8 }}>
                                    {job.error_message}
                                  </div>
                                )}
                                {job.total_records && Object.entries(job.total_records).map(([type, count]) => (
                                  <div key={type} style={{
                                    background: "var(--surface2)", border: "1px solid var(--border)",
                                    borderRadius: "var(--r)", padding: "6px 12px", fontSize: 12,
                                  }}>
                                    <span style={{ fontFamily: "var(--mono)", color: "var(--teal)", marginRight: 6 }}>
                                      {count.toLocaleString()}
                                    </span>
                                    <span style={{ color: "var(--text2)" }}>{type}</span>
                                  </div>
                                ))}
                                <div style={{ width: "100%", marginTop: 4, fontSize: 11, fontFamily: "var(--mono)", color: "var(--text3)" }}>
                                  job/{job.id}
                                </div>
                              </div>
                            </td>
                          </tr>
                        )}
                      </>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              {total > limit && (
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: 16, fontSize: 13, color: "var(--text3)" }}>
                  <span>{offset + 1}–{Math.min(offset + limit, total)} of {total}</span>
                  <div style={{ display: "flex", gap: 8 }}>
                    <button className="btn btn-ghost btn-sm" disabled={offset === 0} onClick={() => setOffset(o => Math.max(0, o - limit))}>← Prev</button>
                    <button className="btn btn-ghost btn-sm" disabled={offset + limit >= total} onClick={() => setOffset(o => o + limit)}>Next →</button>
                  </div>
                </div>
              )}
            </>
          ))}
        </main>
      </div>
    </div>
  );
}
